<template>
  <gas-page-shell :breadcrumb="['告警管理', '告警设置']">
    <div class="search-panel">
      <el-form :inline="true" :model="query" size="small" label-width="72px" class="search-form">
        <el-form-item label="规则名称">
          <el-input v-model="query.keyword" clearable placeholder="请输入告警名称" style="width:180px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="告警类别">
          <el-select v-model="query.alarmCategory" clearable placeholder="全部类别" style="width:140px">
            <el-option v-for="c in categoryOptions" :key="c.value" :label="c.label" :value="c.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="告警级别">
          <el-select v-model="query.alarmLevel" clearable placeholder="全部级别" style="width:120px">
            <el-option v-for="l in levelOptions" :key="l.value" :label="l.label" :value="l.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备类型">
          <el-select v-model="query.deviceType" clearable placeholder="全部类型" style="width:160px">
            <el-option v-for="t in deviceTypeOptions" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备编号">
          <el-input v-model="query.deviceCode" clearable placeholder="设备编号" style="width:160px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="query.enabled" clearable placeholder="全部状态" style="width:120px">
            <el-option label="已启用" value="0" />
            <el-option label="已停用" value="1" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetSearch">重置</el-button>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="stat-row">
      <div v-for="item in statCards" :key="item.key" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }"><i :class="item.icon"></i></div>
        <div class="stat-body">
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value" :style="{ color: item.valueColor || '#1d2129' }">{{ item.value }}</div>
        </div>
      </div>
    </div>

    <div class="table-panel">
      <div class="table-toolbar">
        <div class="category-tabs">
          <span
            v-for="tab in deviceTypeTabs"
            :key="tab.value || 'all'"
            class="cat-tab"
            :class="{ active: activeDeviceTypeTab === tab.value }"
            @click="switchDeviceTypeTab(tab.value)"
          >{{ tab.label }}</span>
        </div>
        <div class="toolbar-actions">
          <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate(emptyForm())">新增告警设置</el-button>
          <el-button size="small" class="btn-enable" icon="el-icon-video-play" :disabled="!selectedIds.length" @click="batchSetEnabled('0')">批量启用</el-button>
          <el-button size="small" class="btn-disable" icon="el-icon-video-pause" :disabled="!selectedIds.length" @click="batchSetEnabled('1')">批量停用</el-button>
          <el-button size="small" icon="el-icon-setting" circle title="刷新列表" @click="refreshAll" />
        </div>
      </div>

      <div class="table-head">
        <div class="table-title-group">
          <span class="table-title">告警规则列表 <em>共 {{ total }} 条记录</em></span>
          <gas-table-toolbar
            :columns="tableColumnDefs"
            :visible-map.sync="tableColumnVisible"
            :selected-count="selectedIds.length"
            :show-batch-delete="tableCanBatchDelete"
            @batch-delete="batchRemove"
          />
        </div>
      </div>

      <el-table
        :data="list"
        v-loading="loading"
        border
        size="small"
        class="rule-table"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="44" align="center" />
        <el-table-column label="编号" width="72" align="center">
          <template slot-scope="{ row }">{{ displayCode(row) }}</template>
        </el-table-column>
        <el-table-column label="告警名称" min-width="160">
          <template slot-scope="{ row }">
            <div class="rule-name">{{ row.ruleName }}</div>
            <div class="rule-desc">{{ row.ruleDesc || row.remark || '—' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="类别" width="96" align="center">
          <template slot-scope="{ row }">
            <span class="cat-tag" :class="'cat-' + (row.alarmCategory || 'default')">{{ categoryLabel(row.alarmCategory) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="监测指标" width="100" align="center">
          <template slot-scope="{ row }">{{ metricLabel(row.metricKey) }}</template>
        </el-table-column>
        <el-table-column label="告警级别" width="88" align="center">
          <template slot-scope="{ row }">
            <span class="level-dot" :class="'level-' + (row.alarmLevel || 'warning')"></span>
            <span>{{ levelLabel(row.alarmLevel) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="阈值范围" min-width="120">
          <template slot-scope="{ row }">
            <div v-for="(line, idx) in thresholdLines(row)" :key="idx" class="threshold-line">{{ line }}</div>
          </template>
        </el-table-column>
        <el-table-column label="绑定设备" min-width="200">
          <template slot-scope="{ row }">
            <div class="exec-line">站点：{{ row.siteName || '全部站点' }}</div>
            <div class="exec-line">类型：{{ row.deviceType || '全部类型' }}</div>
            <div class="exec-line">编号：{{ row.deviceCode || '该类型全部设备' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="报警执行条件" min-width="180">
          <template slot-scope="{ row }">
            <div class="exec-line">{{ row.paramCondition || formatLegacyParam(row) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="72" align="center">
          <template slot-scope="{ row }">
            <el-switch
              v-model="row.enabled"
              active-value="0"
              inactive-value="1"
              @change="val => onToggleEnabled(row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="148" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" class="op-edit" icon="el-icon-edit" @click="openEdit(row, emptyForm())">编辑</el-button>
            <el-button type="text" size="small" class="op-copy" icon="el-icon-document-copy" @click="copyRule(row)">复制</el-button>
            <el-button type="text" size="small" class="op-del" icon="el-icon-delete" @click="removeRow(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :current-page="query.pageNum"
        :page-size="query.pageSize"
        :page-sizes="[10, 20, 50]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="720px" @closed="onDialogClosed" @open="onDialogOpen">
      <el-form ref="ruleForm" :model="form" label-width="108px" size="small">
        <el-form-item label="告警名称" required>
          <el-input v-model="form.ruleName" placeholder="如：浦东站市电远传温度报警" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.ruleDesc" type="textarea" :rows="2" placeholder="规则说明" />
        </el-form-item>

        <div class="form-section-title">设备绑定</div>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="绑定站点">
              <el-select
                v-model="form.siteName"
                clearable
                filterable
                allow-create
                default-first-option
                placeholder="全部站点 / 选择或输入"
                style="width:100%"
                @change="onBindFilterChange"
              >
                <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="设备类型" required>
              <el-select v-model="form.deviceType" clearable filterable placeholder="如 市电远传" style="width:100%" @change="onBindFilterChange">
                <el-option v-for="t in deviceTypeOptions" :key="t" :label="t" :value="t" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="绑定设备">
          <el-select
            v-model="form.deviceCode"
            clearable
            filterable
            placeholder="不选则绑定该类型下全部设备"
            style="width:100%"
            @change="onDevicePick"
          >
            <el-option
              v-for="d in bindDeviceOptions"
              :key="d.deviceId || d.deviceCode"
              :label="`${d.deviceName || d.deviceCode}（${d.deviceCode}）`"
              :value="d.deviceCode"
            />
          </el-select>
          <div class="form-tip">示例：站点 + 市电远传 + 不选具体设备 → 该站点下所有市电远传设备生效</div>
        </el-form-item>

        <div class="form-section-title">监测指标与阈值</div>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="监测指标" required>
              <el-select v-model="form.metricKey" placeholder="请选择指标" style="width:100%" @change="onMetricChange">
                <el-option v-for="m in metricOptions" :key="m.key" :label="`${m.label}（${m.unit}）`" :value="m.key" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="告警类别">
              <el-select v-model="form.alarmCategory" style="width:100%">
                <el-option v-for="c in categoryOptions" :key="c.value" :label="c.label" :value="c.value" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="8">
            <el-form-item label="阈值下限">
              <el-input v-model="form.thresholdLower" placeholder="如 -5" @change="syncParamCondition">
                <template slot="append">{{ currentMetricUnit }}</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="阈值上限">
              <el-input v-model="form.thresholdUpper" placeholder="如 20" @change="syncParamCondition">
                <template slot="append">{{ currentMetricUnit }}</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="告警级别">
              <el-select v-model="form.alarmLevel" style="width:100%">
                <el-option v-for="l in levelOptions" :key="l.value" :label="l.label" :value="l.value" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="扩展阈值">
          <el-input v-model="form.thresholdExtra" placeholder="超时秒数等（可选）" />
        </el-form-item>
        <el-form-item label="确认方式">
          <el-select v-model="form.confirmMethod" style="width:100%">
            <el-option label="自动确认" value="自动确认" />
            <el-option label="人工确认" value="人工确认" />
          </el-select>
        </el-form-item>
        <el-form-item label="执行条件">
          <el-input v-model="form.paramCondition" type="textarea" :rows="2" placeholder="可自动生成，也可手动修改" />
        </el-form-item>
        <el-form-item label="启用状态">
          <el-switch v-model="form.enabled" active-value="0" inactive-value="1" active-text="启用" inactive-text="停用" />
        </el-form-item>
        <el-form-item label="通知渠道"><el-input v-model="form.notifyChannels" placeholder="sms,email,web" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import gasCrudPage from '@/mixins/gasCrudPage'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import { DEVICE_TREND_METRICS, metricByKey } from '@/config/deviceTrendMetrics'
import { METRIC_ALARM_CATEGORY } from '@/utils/monitorAlarmThreshold'
import { gasApi } from '@/api/gas'

const categoryOptions = [
  { value: 'flow', label: '流量类' },
  { value: 'pressure', label: '压力类' },
  { value: 'water_quality', label: '水质类' },
  { value: 'device_status', label: '设备状态' },
  { value: 'temperature', label: '温度类' }
]

const levelOptions = [
  { value: 'critical', label: '紧急' },
  { value: 'warning', label: '重要' },
  { value: 'info', label: '一般' }
]

const emptyForm = () => ({
  enabled: '0',
  alarmCategory: 'temperature',
  alarmLevel: 'warning',
  confirmMethod: '自动确认',
  compareOp: 'gt',
  deviceType: '',
  siteName: '',
  deviceCode: '',
  deviceId: null,
  metricKey: 'temperature',
  thresholdUpper: '',
  thresholdLower: '',
  thresholdExtra: '',
  paramCondition: '',
  notifyChannels: ''
})

export default {
  components: { GasPageShell },
  mixins: [gasCrudPage, deviceTypeOptionsMixin],
  data() {
    return {
      idField: 'ruleId',
      listApi: gasApi.alarmRule.list,
      saveApi: gasApi.alarmRule.save,
      removeApi: gasApi.alarmRule.remove,
      batchRemoveApi: gasApi.alarmRule.batchRemove,
      categoryOptions,
      levelOptions,
      metricOptions: DEVICE_TREND_METRICS,
      activeDeviceTypeTab: '',
      statCards: [],
      siteOptions: [],
      bindDeviceOptions: [],
      query: {
        pageNum: 1,
        pageSize: 10,
        keyword: '',
        enabled: '',
        alarmCategory: '',
        alarmLevel: '',
        deviceType: '',
        deviceCode: ''
      }
    }
  },
  computed: {
    currentMetricUnit() {
      return (metricByKey(this.form.metricKey) || {}).unit || ''
    },
    /** 列表上方 Tab：全部 + 设备类型管理中的类型 */
    deviceTypeTabs() {
      const tabs = [{ value: '', label: '全部' }]
      ;(this.deviceTypeOptions || []).forEach(t => {
        if (t) tabs.push({ value: t, label: t })
      })
      return tabs
    }
  },
  created() {
    this.loadDeviceTypeOptions()
    this.loadSiteOptions()
    this.loadStats()
  },
  methods: {
    emptyForm,
    buildQuery() {
      const p = { ...this.query }
      Object.keys(p).forEach(k => { if (p[k] === '') p[k] = undefined })
      return p
    },
    async loadSiteOptions() {
      try {
        const res = await gasApi.alarm.siteOptions()
        this.siteOptions = res.data || []
      } catch (e) {
        this.siteOptions = []
      }
      // 补充设备列表中的站点
      try {
        const res = await gasApi.device.list({ pageNum: 1, pageSize: 500 })
        const records = (res.data && res.data.records) || []
        const set = new Set(this.siteOptions)
        records.forEach(d => { if (d.siteName) set.add(d.siteName) })
        this.siteOptions = Array.from(set)
      } catch (e) { /* ignore */ }
    },
    async loadBindDevices() {
      try {
        const params = { pageNum: 1, pageSize: 500 }
        if (this.form.siteName) params.siteName = this.form.siteName
        if (this.form.deviceType) params.terminalType = this.form.deviceType
        const res = await gasApi.device.list(params)
        this.bindDeviceOptions = (res.data && res.data.records) || []
      } catch (e) {
        this.bindDeviceOptions = []
      }
    },
    onDialogOpen() {
      this.loadBindDevices()
      if (!this.form.paramCondition) this.syncParamCondition()
    },
    onDialogClosed() {
      this.form = {}
      this.bindDeviceOptions = []
    },
    onBindFilterChange() {
      this.form.deviceCode = ''
      this.form.deviceId = null
      this.loadBindDevices()
    },
    onDevicePick(code) {
      const hit = this.bindDeviceOptions.find(d => d.deviceCode === code)
      if (hit) {
        this.form.deviceId = hit.deviceId
        if (!this.form.siteName && hit.siteName) this.form.siteName = hit.siteName
        if (!this.form.deviceType && hit.terminalType) this.form.deviceType = hit.terminalType
      } else {
        this.form.deviceId = null
      }
    },
    onMetricChange(key) {
      const cat = METRIC_ALARM_CATEGORY[key]
      if (cat) this.form.alarmCategory = cat
      this.syncParamCondition()
    },
    syncParamCondition() {
      const m = metricByKey(this.form.metricKey)
      if (!m) return
      const lower = this.form.thresholdLower
      const upper = this.form.thresholdUpper
      const unit = m.unit || ''
      if (lower !== '' && lower != null && upper !== '' && upper != null) {
        this.form.paramCondition = `${m.label} < ${lower}${unit} 或 > ${upper}${unit}`
      } else if (upper !== '' && upper != null) {
        this.form.paramCondition = `${m.label} > ${upper}${unit}`
      } else if (lower !== '' && lower != null) {
        this.form.paramCondition = `${m.label} < ${lower}${unit}`
      }
    },
    async loadStats() {
      try {
        const res = await gasApi.alarmRule.stats()
        const d = res.data || {}
        this.statCards = [
          { key: 'total', label: '全部规则', value: d.total || 0, icon: 'el-icon-s-order', color: '#1677ff', bg: '#e8f3ff' },
          { key: 'enabled', label: '已启用', value: d.enabled || 0, icon: 'el-icon-open', color: '#00b42a', bg: '#e8ffea', valueColor: '#00b42a' },
          { key: 'disabled', label: '已停用', value: d.disabled || 0, icon: 'el-icon-turn-off', color: '#86909c', bg: '#f2f3f5' },
          { key: 'critical', label: '紧急级别', value: d.critical || 0, icon: 'el-icon-bell', color: '#f53f3f', bg: '#ffece8', valueColor: '#f53f3f' },
          { key: 'triggered', label: '今日触发', value: d.triggeredToday || 0, icon: 'el-icon-lightning', color: '#ff7d00', bg: '#fff7e8', valueColor: '#ff7d00' }
        ]
      } catch (e) {
        this.statCards = []
      }
    },
    async loadData() {
      if (!this.listApi) return
      this.loading = true
      try {
        const res = await this.listApi(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    refreshAll() {
      this.loadStats()
      this.loadData()
    },
    search() {
      this.query.pageNum = 1
      this.activeDeviceTypeTab = this.query.deviceType || ''
      this.loadStats()
      this.loadData()
    },
    resetSearch() {
      this.query = {
        pageNum: 1,
        pageSize: this.query.pageSize,
        keyword: '',
        enabled: '',
        alarmCategory: '',
        alarmLevel: '',
        deviceType: '',
        deviceCode: ''
      }
      this.activeDeviceTypeTab = ''
      this.loadStats()
      this.loadData()
    },
    switchDeviceTypeTab(val) {
      this.activeDeviceTypeTab = val
      this.query.deviceType = val
      this.query.pageNum = 1
      this.loadData()
    },
    displayCode(row) {
      return row.ruleCode || ('R' + String(row.ruleId || 0).padStart(3, '0'))
    },
    categoryLabel(val) {
      const hit = categoryOptions.find(c => c.value === val)
      return hit ? hit.label : val || '—'
    },
    levelLabel(val) {
      const hit = levelOptions.find(l => l.value === val)
      return hit ? hit.label : '重要'
    },
    metricLabel(key) {
      if (!key) return '—'
      return (metricByKey(key) || {}).label || key
    },
    thresholdLines(row) {
      const unit = row.metricKey ? ((metricByKey(row.metricKey) || {}).unit || '') : this.unitHint(row.alarmCategory)
      const lines = []
      if (row.thresholdUpper !== '' && row.thresholdUpper != null) lines.push(`上限: ${row.thresholdUpper}${unit ? ' ' + unit : ''}`)
      if (row.thresholdLower !== '' && row.thresholdLower != null) lines.push(`下限: ${row.thresholdLower}${unit ? ' ' + unit : ''}`)
      if (row.thresholdExtra) lines.push(`超时时长: ${row.thresholdExtra}秒`)
      if (!lines.length && row.thresholdValue) {
        lines.push(`${row.compareOp || '>'} ${row.thresholdValue}`)
      }
      return lines.length ? lines : ['—']
    },
    unitHint(cat) {
      if (cat === 'flow') return 'm³/h'
      if (cat === 'pressure') return 'MPa'
      if (cat === 'temperature') return '℃'
      return ''
    },
    formatLegacyParam(row) {
      if (row.paramCondition) return row.paramCondition
      if (!row.thresholdValue) return '—'
      return `${row.alarmType || '参数'} ${row.compareOp || '>'} ${row.thresholdValue}`
    },
    async onToggleEnabled(row, enabled) {
      try {
        await gasApi.alarmRule.setEnabled(row.ruleId, enabled)
        this.$message.success(enabled === '0' ? '已启用' : '已停用')
        this.loadStats()
      } catch (e) {
        row.enabled = enabled === '0' ? '1' : '0'
      }
    },
    async batchSetEnabled(enabled) {
      if (!this.selectedIds.length) return
      const action = enabled === '0' ? '启用' : '停用'
      await this.$confirm(`确认批量${action}选中的 ${this.selectedIds.length} 条规则？`, '提示', { type: 'warning' })
      await gasApi.alarmRule.batchEnabled({ ruleIds: this.selectedIds, enabled })
      this.$message.success(`批量${action}成功`)
      this.refreshAll()
    },
    async copyRule(row) {
      await gasApi.alarmRule.copy(row.ruleId)
      this.$message.success('复制成功')
      this.refreshAll()
    },
    async submitForm() {
      if (!this.form.ruleName) return this.$message.warning('请填写告警名称')
      if (!this.form.deviceType) return this.$message.warning('请选择设备类型')
      if (!this.form.metricKey) return this.$message.warning('请选择监测指标')
      if (this.form.thresholdLower === '' && this.form.thresholdUpper === '') {
        return this.$message.warning('请至少填写阈值上限或下限')
      }
      this.syncParamCondition()
      await this.saveApi(this.form)
      this.$message.success('保存成功')
      this.dialogVisible = false
      this.refreshAll()
    },
    afterSave() {
      this.loadStats()
    }
  }
}
</script>

<style scoped>
.search-panel {
  background: #fff;
  border-radius: 8px;
  padding: 16px 16px 4px;
  margin-bottom: 16px;
}
.search-form >>> .el-form-item {
  margin-bottom: 12px;
}
.stat-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}
.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}
.stat-label {
  color: #86909c;
  font-size: 13px;
}
.stat-value {
  font-size: 22px;
  font-weight: 600;
  margin-top: 4px;
}
.table-panel {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
}
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.cat-tab {
  padding: 6px 14px;
  font-size: 13px;
  color: #4e5969;
  background: #f2f3f5;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}
.cat-tab.active {
  color: #1677ff;
  background: #e8f3ff;
  font-weight: 500;
}
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.btn-enable {
  color: #00b42a;
  border-color: #00b42a;
}
.btn-disable {
  color: #86909c;
}
.table-head {
  margin-bottom: 8px;
}
.table-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}
.table-title em {
  font-style: normal;
  font-weight: 400;
  color: #86909c;
  font-size: 13px;
  margin-left: 8px;
}
.rule-name {
  font-weight: 600;
  color: #1d2129;
  line-height: 1.4;
}
.rule-desc {
  font-size: 12px;
  color: #86909c;
  margin-top: 4px;
  line-height: 1.35;
}
.cat-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 20px;
}
.cat-flow { background: #e8f3ff; color: #1677ff; }
.cat-pressure { background: #f9f0ff; color: #722ed1; }
.cat-water_quality { background: #e8fffb; color: #14c9c9; }
.cat-device_status { background: #fff7e8; color: #ff7d00; }
.cat-temperature { background: #f2f3f5; color: #4e5969; }
.cat-default { background: #f2f3f5; color: #86909c; }
.level-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  vertical-align: middle;
}
.level-critical { background: #f53f3f; }
.level-warning { background: #ff7d00; }
.level-info { background: #c9cdd4; }
.threshold-line {
  font-size: 12px;
  color: #f77234;
  line-height: 1.5;
}
.exec-line {
  font-size: 12px;
  color: #4e5969;
  line-height: 1.45;
}
.form-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1d2129;
  margin: 8px 0 12px;
  padding-left: 8px;
  border-left: 3px solid #1677ff;
}
.form-tip {
  font-size: 12px;
  color: #86909c;
  line-height: 1.4;
  margin-top: 4px;
}
.op-edit { color: #1677ff; }
.op-copy { color: #86909c; }
.op-del { color: #f53f3f; }
.pager {
  margin-top: 16px;
  text-align: right;
}
@media (max-width: 1200px) {
  .stat-row {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
